/**
 * Optical Path Tracer - Traces fiber path from OLT to any point in the network
 * Calculates signal power and loss along the path
 */

import { Pole, Cable, SplitterType, CassetteConfig } from '../types';
import { getSplitterLoss, getSplitterOutputLoss } from './splitterCatalog';
// Útlm sa počíta z OPTICKEJ dĺžky = úsek + návin na koncoch. Návin je
// reálne vlákno navyše (zostup do krabice, stočená rezerva), preto sa
// nesmie vynechať. Doplnené 2026-09-30.
import { cableOpticalLength } from './cableLength';

export interface OpticalPathSegment {
  type: 'olt_port' | 'olt_splitter' | 'cable' | 'splice' | 'splitter' | 'passthrough' | 'tap';
  poleId?: number;
  poleName?: string;
  cableId?: number;
  cableName?: string;
  fiber?: number;
  loss: number;
  distance?: number;
  power: number; // Power at the END of this segment
  description: string;
}

export interface OpticalPathResult {
  found: boolean;
  segments: OpticalPathSegment[];
  totalLoss: number;
  finalPower: number;
  oltPower: number;
  isValid: boolean; // true if path is complete from OLT
  errorMessage?: string;
}

const SPLITTER_LOSS: Record<SplitterType, number> = {
  '1:2': 3.5,
  '1:4': 7.0,
  '1:8': 10.5,
  '1:16': 13.5,
  '1:32': 17.0,
  '1:64': 20.5,
};

/**
 * Trasovanie sa volá aj v slučke — pri výbere vlákna v kazete raz na každé
 * vlákno kábla, čo je pri 144-vláknovom kábli 144 volaní. Každé z nich písalo
 * desiatky riadkov do konzoly, takže sa v nej nedalo nič nájsť. Zapni na true,
 * keď treba ladiť optickú cestu.
 */
const DEBUG_TRACE = false;
const log = (...a: any[]) => { if (DEBUG_TRACE) console.log(...a); };

const FIBER_LOSS_PER_KM = 0.35; // dB/km
const SPLICE_LOSS = 0.1; // dB
const CONNECTOR_LOSS = 0.5; // dB

/**
 * Determine the direction of a fiber on a cable
 * Phase 2: Automatic detection of reverse fibers based on splitter configuration
 */
function getFiberDirection(
  cableId: number,
  fiberNumber: number,
  cables: Cable[],
  poles: Pole[]
): {
  sourcePoleId: number;
  targetPoleId: number;
  isReverse: boolean;
} | null {
  const cable = cables.find(c => c.id === cableId);
  if (!cable) {
    return null;
  }

  // Check if this fiber should be reversed based on splitter configuration
  const isReverse = shouldFiberBeReversed(cableId, fiberNumber, poles);

  if (isReverse) {
    // Reverse direction: to_pole → from_pole
    return {
      sourcePoleId: cable.to_pole_id,
      targetPoleId: cable.from_pole_id,
      isReverse: true,
    };
  } else {
    // Normal direction: from_pole → to_pole
    return {
      sourcePoleId: cable.from_pole_id,
      targetPoleId: cable.to_pole_id,
      isReverse: false,
    };
  }
}

/**
 * Check if a fiber should be reversed based on manual checkbox setting
 * A fiber is reversed if it's marked as reverse_direction in splitter output, splice output, or passthrough output
 */
function shouldFiberBeReversed(cableId: number, fiberNumber: number, poles: Pole[]): boolean {
  for (const pole of poles) {
    // Check splitter outputs
    if (pole.cassette?.splitters) {
      for (const splitter of pole.cassette.splitters) {
        // Check if this fiber is used as output with reverse_direction flag
        const reverseOutput = splitter.outputs?.find(output =>
          output.cable_id === cableId &&
          output.fiber === fiberNumber &&
          output.reverse_direction === true
        );

        if (reverseOutput) {
          return true; // This fiber is manually marked as reversed
        }
      }
    }

    // Check splice outputs
    if (pole.cassette?.splices) {
      for (const splice of pole.cassette.splices) {
        if (splice.to_cable_id === cableId &&
            splice.to_fiber === fiberNumber &&
            splice.reverse_direction === true) {
          return true; // This fiber is manually marked as reversed
        }
      }
    }

    // Passthrough is always bidirectional - no need to check reverse_direction flag
  }

  return false; // Normal direction
}

/**
 * Trace optical path from OLT to a specific fiber on a cable
 */
export function traceOpticalPath(
  targetCableId: number,
  targetFiber: number,
  poles: Pole[],
  cables: Cable[]
): OpticalPathResult {
  const segments: OpticalPathSegment[] = [];
  let currentPower = 0;
  let totalLoss = 0;

  log(`[traceOpticalPath] 🔍 Tracing cable=${targetCableId}, fiber=${targetFiber}`);

  // Determine fiber direction (including reverse fiber detection)
  const fiberDirection = getFiberDirection(targetCableId, targetFiber, cables, poles);
  if (!fiberDirection) {
    log(`[traceOpticalPath] ❌ Kábel #${targetCableId} sa nenašiel`);
    return {
      found: false,
      segments: [],
      totalLoss: 0,
      finalPower: 0,
      oltPower: 0,
      isValid: false,
      errorMessage: `Kábel #${targetCableId} sa nenašiel`,
    };
  }

  log(`[traceOpticalPath] 🔍 Fiber direction:`, fiberDirection);

  // Find the source pole for this fiber (where the fiber starts)
  const sourcePole = poles.find(p => p.id === fiberDirection.sourcePoleId);
  if (!sourcePole) {
    log(`[traceOpticalPath] ❌ Zdrojový stĺpik #${fiberDirection.sourcePoleId} sa nenašiel`);
    return {
      found: false,
      segments: [],
      totalLoss: 0,
      finalPower: 0,
      oltPower: 0,
      isValid: false,
      errorMessage: `Zdrojový stĺpik #${fiberDirection.sourcePoleId} sa nenašiel`,
    };
  }

  log(`[traceOpticalPath] 🔍 Source pole: ${sourcePole.name} (${sourcePole.id}), type=${sourcePole.pole_type}`);

  // Check if this cable is an OUTPUT from the source pole
  // (i.e., the fiber is generated at source pole via splitter/splice)
  const visited = new Set<string>();
  log(`[traceOpticalPath] 🔍 Calling traceFromSource for ${sourcePole.name}`);
  let result = traceFromSource(
    sourcePole,
    targetCableId,
    targetFiber,
    poles,
    cables,
    visited
  );

  // If not found, try the other end of the cable (for passthrough case)
  if (!result.found) {
    log(`[traceOpticalPath] ⚠️ No path found from ${sourcePole.name}, trying other end of cable`);

    const cable = cables.find(c => c.id === targetCableId);
    if (cable) {
      const otherPoleId = fiberDirection.sourcePoleId === cable.from_pole_id ? cable.to_pole_id : cable.from_pole_id;
      const otherPole = poles.find(p => p.id === otherPoleId);

      if (otherPole) {
        log(`[traceOpticalPath] 🔍 Trying from ${otherPole.name} (${otherPole.id})`);
        const visited2 = new Set<string>();
        result = traceFromSource(
          otherPole,
          targetCableId,
          targetFiber,
          poles,
          cables,
          visited2
        );
      }
    }
  }

  if (!result.found) {
    log(`[traceOpticalPath] ❌ No path found: ${result.errorMessage}`);
    return {
      found: false,
      segments: [],
      totalLoss: 0,
      finalPower: 0,
      oltPower: 0,
      isValid: false,
      errorMessage: result.errorMessage || 'Trasa k OLT sa nenašla',
    };
  }

  log(`[traceOpticalPath] ✅ Path found! Segments: ${result.segments.length}, OLT power: ${result.oltPower} dBm`);

  // Reverse segments (we traced backwards, now show forward)
  const forwardSegments = result.segments.reverse();

  // Calculate power at each segment
  currentPower = result.oltPower;
  totalLoss = 0;

  forwardSegments.forEach(segment => {
    totalLoss += segment.loss;
    currentPower -= segment.loss;
    segment.power = currentPower;
  });

  return {
    found: true,
    segments: forwardSegments,
    totalLoss,
    finalPower: currentPower,
    oltPower: result.oltPower,
    isValid: true,
  };
}

interface TraceResult {
  found: boolean;
  segments: OpticalPathSegment[];
  oltPower: number;
  errorMessage?: string;
}

/**
 * Trace a cascade of splitters within the same cassette
 * Recursively follows splitter_output connections until reaching a cable input or OLT port
 */
function traceSplitterCascade(
  pole: Pole,
  splitter: any,
  poles: Pole[],
  cables: Cable[],
  visited: Set<string>
): TraceResult {
  const cassette = pole.cassette;
  if (!cassette) {
    return {
      found: false,
      segments: [],
      oltPower: 0,
      errorMessage: `Stĺpik ${pole.name} nemá kazetu`,
    };
  }

  if (splitter.input_source_type === 'cable') {
    // Base case: splitter is fed from a cable
    const inputCableId = splitter.input_cable_id!;
    const inputFiber = splitter.input_fiber!;
    const inputCable = cables.find(c => c.id === inputCableId);

    if (!inputCable) {
      return {
        found: false,
        segments: [],
        oltPower: 0,
        errorMessage: `Vstupný kábel #${inputCableId} sa nenašiel`,
      };
    }

    // Determine the direction of the input fiber
    const inputFiberDirection = getFiberDirection(inputCableId, inputFiber, cables, poles);
    if (!inputFiberDirection) {
      return {
        found: false,
        segments: [],
        oltPower: 0,
        errorMessage: `Nedá sa určiť smer vstupného kábla #${inputCableId}, vlákno #${inputFiber}`,
      };
    }

    // Use the SOURCE pole of the input fiber (where the fiber originates)
    const upstreamPole = poles.find(p => p.id === inputFiberDirection.sourcePoleId);
    if (!upstreamPole) {
      return {
        found: false,
        segments: [],
        oltPower: 0,
        errorMessage: `Nadradený stĺpik #${inputFiberDirection.sourcePoleId} sa nenašiel`,
      };
    }

    // Recursively trace from upstream pole
    const upstreamResult = traceFromSource(
      upstreamPole,
      inputCableId,
      inputFiber,
      poles,
      cables,
      visited
    );

    if (!upstreamResult.found) {
      return upstreamResult;
    }

    // Add this splitter segment
    const splitterLoss = getSplitterLoss(splitter.type);
    const splitterSegment: OpticalPathSegment = {
      type: 'splitter',
      poleId: pole.id,
      poleName: pole.name,
      loss: splitterLoss,
      power: 0,
      description: `Splitter ${splitter.type} at ${pole.name}`,
    };

    return {
      found: true,
      segments: [...upstreamResult.segments, splitterSegment],
      oltPower: upstreamResult.oltPower,
    };
  } else if (splitter.input_source_type === 'splitter_output') {
    // Recursive case: splitter is fed from another splitter
    const sourceSplitterIndex = splitter.input_splitter_index;
    const sourceOutputIndex = splitter.input_splitter_output;

    log(`[traceSplitterCascade] Tracing splitter fed from splitter #${sourceSplitterIndex} output #${sourceOutputIndex}`);

    if (sourceSplitterIndex === undefined || sourceOutputIndex === undefined) {
      console.error('[traceSplitterCascade] Neplatné nastavenie výstupu splittera');
      return {
        found: false,
        segments: [],
        oltPower: 0,
        errorMessage: 'Neplatné nastavenie výstupu splittera',
      };
    }

    // ✅ CYCLE DETECTION: Check if we're trying to trace the same splitter (self-reference)
    const currentSplitterIndex = cassette.splitters?.indexOf(splitter);
    if (currentSplitterIndex === sourceSplitterIndex) {
      console.error(`[traceSplitterCascade] ❌ CYCLE DETECTED: Splitter #${currentSplitterIndex} references itself!`);
      return {
        found: false,
        segments: [],
        oltPower: 0,
        errorMessage: `Cyklus: splitter #${currentSplitterIndex} odkazuje sám na seba`,
      };
    }

    // ✅ CYCLE DETECTION: Check if we've already visited this splitter
    const visitKey = `${pole.id}-splitter-${sourceSplitterIndex}`;
    if (visited.has(visitKey)) {
      console.error(`[traceSplitterCascade] ❌ CYCLE DETECTED: Already visited splitter #${sourceSplitterIndex} at pole ${pole.id}`);
      return {
        found: false,
        segments: [],
        oltPower: 0,
        errorMessage: `Cyklus: splitter #${sourceSplitterIndex} sa už použil`,
      };
    }
    visited.add(visitKey);

    const sourceSplitter = cassette.splitters?.[sourceSplitterIndex];
    if (!sourceSplitter) {
      console.error(`[traceSplitterCascade] Zdrojový splitter #${sourceSplitterIndex} sa nenašiel`);
      return {
        found: false,
        segments: [],
        oltPower: 0,
        errorMessage: `Zdrojový splitter #${sourceSplitterIndex} sa nenašiel`,
      };
    }

    log(`[traceSplitterCascade] Found source splitter type: ${sourceSplitter.type}, input_source_type: ${sourceSplitter.input_source_type}`);

    // Recursively trace the source splitter
    const sourceResult = traceSplitterCascade(
      pole,
      sourceSplitter,
      poles,
      cables,
      visited
    );

    if (!sourceResult.found) {
      console.error(`[traceSplitterCascade] Failed to trace source splitter: ${sourceResult.errorMessage}`);
      return sourceResult;
    }

    log(`[traceSplitterCascade] Successfully traced source splitter, adding current splitter to path`);

    // Add this splitter segment
    const splitterLoss = getSplitterLoss(splitter.type);
    const splitterSegment: OpticalPathSegment = {
      type: 'splitter',
      poleId: pole.id,
      poleName: pole.name,
      loss: splitterLoss,
      power: 0,
      description: `Splitter ${splitter.type} at ${pole.name}`,
    };

    return {
      found: true,
      segments: [...sourceResult.segments, splitterSegment],
      oltPower: sourceResult.oltPower,
    };
  } else if (splitter.input_source_type === 'olt_port') {
    // Base case for OLT: splitter is fed from OLT port
    if (pole.pole_type !== 'olt') {
      return {
        found: false,
        segments: [],
        oltPower: 0,
        errorMessage: 'Vstup olt_port na stĺpiku, ktorý nie je OLT',
      };
    }

    const portNumber = splitter.input_olt_port || 1;
    const portConfig = cassette.olt_port_configs?.find(pc => pc.port_number === portNumber);
    const oltPower = portConfig?.power_dbm || 3.0;

    const splitterLoss = getSplitterLoss(splitter.type);

    const segments: OpticalPathSegment[] = [
      {
        type: 'olt_port',
        poleId: pole.id,
        poleName: pole.name,
        loss: 0,
        power: oltPower,
        description: `OLT ${pole.name} Port ${portNumber} (${oltPower} dBm)`,
      },
      {
        type: 'olt_splitter',
        poleId: pole.id,
        poleName: pole.name,
        loss: splitterLoss,
        power: 0,
        description: `OLT Splitter ${splitter.type} (Port ${portNumber})`,
      },
    ];

    return {
      found: true,
      segments,
      oltPower,
    };
  }

  return {
    found: false,
    segments: [],
    oltPower: 0,
    errorMessage: `Neznámy typ vstupu splittera: ${splitter.input_source_type}`,
  };
}

/**
 * Trace from source pole to see if this cable/fiber is connected to OLT
 * This checks if the fiber is an OUTPUT from the source pole
 */
function traceFromSource(
  sourcePole: Pole,
  outgoingCableId: number,
  outgoingFiber: number,
  poles: Pole[],
  cables: Cable[],
  visited: Set<string>
): TraceResult {
  log(`[traceFromSource] 🔍 Pole: ${sourcePole.name}, outgoing cable=${outgoingCableId}, fiber=${outgoingFiber}`);

  const visitKey = `${sourcePole.id}-out-${outgoingCableId}-${outgoingFiber}`;
  if (visited.has(visitKey)) {
    log(`[traceFromSource] ❌ Zistený kruhový odkaz`);
    return {
      found: false,
      segments: [],
      oltPower: 0,
      errorMessage: 'Zistený kruhový odkaz',
    };
  }
  visited.add(visitKey);

  // Check if this is an OLT
  if (sourcePole.pole_type === 'olt') {
    log(`[traceFromSource] 🔍 This is an OLT, calling traceFromOLT`);
    return traceFromOLT(sourcePole, outgoingCableId, outgoingFiber, cables);
  }

  // Check cassette for connections
  const cassette = sourcePole.cassette;
  if (!cassette) {
    log(`[traceFromSource] ❌ Stĺpik ${sourcePole.name} nemá kazetu`);
    return {
      found: false,
      segments: [],
      oltPower: 0,
      errorMessage: `Stĺpik ${sourcePole.name} nemá kazetu`,
    };
  }

  log(`[traceFromSource] 🔍 Cassette has ${cassette.splitters?.length || 0} splitters, ${cassette.splices?.length || 0} splices`);

  // Check if this fiber is an OUTPUT from a splitter
  if (cassette.splitters) {
    log(`[traceFromSource] 🔍 Checking ${cassette.splitters.length} splitters for output cable=${outgoingCableId}, fiber=${outgoingFiber}`);
    for (let i = 0; i < cassette.splitters.length; i++) {
      const splitter = cassette.splitters[i];
      log(`[traceFromSource] 🔍 Splitter #${i}: type=${splitter.type}, outputs=${splitter.outputs?.length || 0}`);

      // Check if outgoingCable is an output from this splitter
      const outputIndex = splitter.outputs?.findIndex(
        out => out.cable_id === outgoingCableId && out.fiber === outgoingFiber
      );

      if (outputIndex !== undefined && outputIndex >= 0) {
        log(`[traceFromSource] ✅ Found! Output #${outputIndex} from splitter #${i}`);
        // Found! This fiber is an output from this splitter
        const splitterLoss = getSplitterOutputLoss(splitter.type, outputIndex);

        // Add cable segment first (from source pole to destination)
        const outgoingCable = cables.find(c => c.id === outgoingCableId);
        const cableLoss = outgoingCable ? cableOpticalLength(outgoingCable) * (FIBER_LOSS_PER_KM / 1000) : 0;

        const cableSegment: OpticalPathSegment = {
          type: 'cable',
          cableId: outgoingCableId,
          cableName: `Cable #${outgoingCableId}`,
          fiber: outgoingFiber,
          loss: cableLoss,
          distance: outgoingCable?.length_meters,
          power: 0,
          description: `Cable #${outgoingCableId}, Fiber #${outgoingFiber} (${outgoingCable?.length_meters || 0}m)`,
        };

        const splitterSegment: OpticalPathSegment = {
          type: 'splitter',
          poleId: sourcePole.id,
          poleName: sourcePole.name,
          loss: splitterLoss,
          power: 0,
          description: `Splitter ${splitter.type} at ${sourcePole.name}`,
        };

        // Now trace the input of this splitter
        log(`[traceFromSource] 🔍 Splitter input_source_type: ${splitter.input_source_type}`);

        if (splitter.input_source_type === 'olt_port') {
          // This shouldn't happen for non-OLT poles
          log(`[traceFromSource] ❌ Invalid: OLT port on non-OLT pole`);
          return {
            found: false,
            segments: [],
            oltPower: 0,
            errorMessage: 'Neplatný vstup splittera (OLT port na stĺpiku, ktorý nie je OLT)',
          };
        } else if (splitter.input_source_type === 'splitter_output') {
          log(`[traceFromSource] 🔍 Input from splitter_output, calling traceSplitterCascade`);
          // Input comes from another splitter in the same cassette
          // Recursively trace the cascade of splitters
          const cascadeResult = traceSplitterCascade(
            sourcePole,
            splitter,
            poles,
            cables,
            visited
          );

          if (!cascadeResult.found) {
            log(`[traceFromSource] ❌ Cascade failed: ${cascadeResult.errorMessage}`);
            return cascadeResult;
          }

          log(`[traceFromSource] ✅ Cascade successful, adding cable segment`);
          // Note: cascadeResult.segments already includes splitterSegment
          // (added by traceSplitterCascade), so we only add cableSegment here
          return {
            found: true,
            segments: [...cascadeResult.segments, cableSegment],
            oltPower: cascadeResult.oltPower,
          };
        } else if (splitter.input_source_type === 'cable') {
          log(`[traceFromSource] 🔍 Input from cable: ${splitter.input_cable_id}, fiber: ${splitter.input_fiber}`);
          const inputCableId = splitter.input_cable_id!;
          const inputFiber = splitter.input_fiber!;
          const inputCable = cables.find(c => c.id === inputCableId);

          if (!inputCable) {
            return {
              found: false,
              segments: [],
              oltPower: 0,
              errorMessage: `Vstupný kábel #${inputCableId} sa nenašiel`,
            };
          }

          // Try both ends of the input cable
          // We don't know which end has the signal source (could be passthrough in reverse direction)
          log(`[traceFromSource] 🔍 Trying both ends of input cable #${inputCableId}, fiber #${inputFiber}`);

          const fromPoleId = inputCable.from_pole_id;
          const toPoleId = inputCable.to_pole_id;

          // Skip the current pole to avoid circular reference
          const polesToTry = [fromPoleId, toPoleId].filter(poleId => poleId !== sourcePole.id);

          log(`[traceFromSource] 🔍 Poles to try: ${polesToTry.join(', ')} (skipping current pole ${sourcePole.id})`);

          for (const poleId of polesToTry) {
            const upstreamPole = poles.find(p => p.id === poleId);
            if (!upstreamPole) {
              log(`[traceFromSource] ⚠️ Pole #${poleId} not found, skipping`);
              continue;
            }

            log(`[traceFromSource] 🔍 Trying upstream pole: ${upstreamPole.name} (${upstreamPole.id})`);

            const upstreamResult = traceFromSource(
              upstreamPole,
              inputCableId,
              inputFiber,
              poles,
              cables,
              visited
            );

            if (upstreamResult.found) {
              log(`[traceFromSource] ✅ Upstream trace successful from ${upstreamPole.name}`);
              return {
                found: true,
                segments: [...upstreamResult.segments, splitterSegment, cableSegment],
                oltPower: upstreamResult.oltPower,
              };
            }

            log(`[traceFromSource] ⚠️ Upstream trace failed from ${upstreamPole.name}: ${upstreamResult.errorMessage}`);
          }

          // If we get here, both ends failed
          log(`[traceFromSource] ❌ Both ends of input cable failed`);
          return {
            found: false,
            segments: [],
            oltPower: 0,
            errorMessage: `Trasa sa nenašla ani z jedného konca vstupného kábla #${inputCableId}, vlákno #${inputFiber}`,
          };
        }
      }
    }
  }

  // Check if this fiber is an OUTPUT from a splice
  if (cassette.splices) {
    for (const splice of cassette.splices) {
      if (splice.to_cable_id === outgoingCableId && splice.to_fiber === outgoingFiber) {
        // Found! This fiber is output from this splice
        const spliceLoss = splice.loss_db || 0.1;

        const outgoingCable = cables.find(c => c.id === outgoingCableId);
        const cableLoss = outgoingCable ? cableOpticalLength(outgoingCable) * (FIBER_LOSS_PER_KM / 1000) : 0;

        const cableSegment: OpticalPathSegment = {
          type: 'cable',
          cableId: outgoingCableId,
          cableName: `Cable #${outgoingCableId}`,
          fiber: outgoingFiber,
          loss: cableLoss,
          distance: outgoingCable?.length_meters,
          power: 0,
          description: `Cable #${outgoingCableId}, Fiber #${outgoingFiber} (${outgoingCable?.length_meters || 0}m)`,
        };

        const spliceSegment: OpticalPathSegment = {
          type: 'splice',
          poleId: sourcePole.id,
          poleName: sourcePole.name,
          loss: spliceLoss,
          power: 0,
          description: `Splice at ${sourcePole.name}`,
        };

        // Trace the input of this splice
        if (splice.from_source_type === 'olt_port') {
          // This shouldn't happen for non-OLT poles
          return {
            found: false,
            segments: [],
            oltPower: 0,
            errorMessage: 'Neplatný vstup zvaru (OLT port na stĺpiku, ktorý nie je OLT)',
          };
        } else if (splice.from_source_type === 'cable') {
          const inputCableId = splice.from_cable_id!;
          const inputFiber = splice.from_fiber!;
          const inputCable = cables.find(c => c.id === inputCableId);

          if (!inputCable) {
            return {
              found: false,
              segments: [],
              oltPower: 0,
              errorMessage: `Vstupný kábel #${inputCableId} sa nenašiel`,
            };
          }

          // Determine the direction of the input fiber
          const inputFiberDirection = getFiberDirection(inputCableId, inputFiber, cables, poles);
          if (!inputFiberDirection) {
            return {
              found: false,
              segments: [],
              oltPower: 0,
              errorMessage: `Nedá sa určiť smer vstupného kábla #${inputCableId}, vlákno #${inputFiber}`,
            };
          }

          // Skúšame OBA konce vstupného kábla, nie len ten podľa nominálneho
          // smeru. Rovnako to robí vetva pre splittery a z rovnakého dôvodu:
          // signál môže prichádzať z druhej strany cez priechod, ktorý je
          // fyzicky obojsmerný a reverzný smer si nikde neoznačuje. Bez toho
          // sa zvar na konci reverznej vetvy javil ako slepá ulica — napr.
          // Stlpik_57, kam signál chodí od Šachty_61 cez Stlpik_59.
          // Najprv sa skúša koniec podľa smeru, aby sa bežné zapojenie
          // vyhodnotilo rovnako ako doteraz.
          // Aktuálny stĺp sa NEVYNECHÁVA: pri zvare je často správnou odpoveďou
          // práve on — signál ide zvarom na iný kábel a hneď v tej istej kazete
          // pokračuje priechodom. Rekurzia je s INÝM káblom a vláknom, takže
          // `visited` (kľúč stĺp+kábel+vlákno) zacyklenie aj tak zachytí.
          const poradieKoncov = [
            inputFiberDirection.sourcePoleId,
            inputFiberDirection.targetPoleId,
          ].filter((id, i, zoznam) => zoznam.indexOf(id) === i);

          let upstreamResult: TraceResult = {
            found: false,
            segments: [],
            oltPower: 0,
            errorMessage: `Nadradený stĺpik #${inputFiberDirection.sourcePoleId} sa nenašiel`,
          };

          for (const poleId of poradieKoncov) {
            const upstreamPole = poles.find(p => p.id === poleId);
            if (!upstreamPole) continue;
            log(`[traceFromSource] 🔍 Zvar: skúšam koniec ${upstreamPole.name}`);
            // KÓPIA navštívených uzlov pre každý pokus. Pri zdieľanom zozname
            // si neúspešný pokus označí uzly ako navštívené a ten ďalší ich
            // potom vyhodnotí ako kruhový odkaz — v produkcii tak dve vlákna,
            // ktoré predtým signál mali, o neho prišli.
            const navstiveneProPokus = new Set(visited);
            upstreamResult = traceFromSource(
              upstreamPole,
              inputCableId,
              inputFiber,
              poles,
              cables,
              navstiveneProPokus
            );
            if (upstreamResult.found) {
              navstiveneProPokus.forEach((k) => visited.add(k));
              break;
            }
          }

          if (!upstreamResult.found) {
            return upstreamResult;
          }

          return {
            found: true,
            segments: [...upstreamResult.segments, spliceSegment, cableSegment],
            oltPower: upstreamResult.oltPower,
          };
        }
      }
    }
  }

  // Check pass-through cables
  if (cassette.pass_through_cables) {
    log(`[traceFromSource] 🔍 Checking ${cassette.pass_through_cables.length} passthroughs for outgoing cable=${outgoingCableId}, fiber=${outgoingFiber}`);

    for (const passThrough of cassette.pass_through_cables) {
      log(`[traceFromSource] 🔍 Passthrough: FROM cable=${passThrough.from_cable_id} → TO cable=${passThrough.to_cable_id}`);

      // Case 1: Signal comes on TO cable, goes back on FROM cable (reverse direction)
      if (passThrough.to_cable_id === outgoingCableId) {
        const fiberConfig = passThrough.fibers?.find(f => f.fiber_number === outgoingFiber);
        log(`[traceFromSource] 🔍 Case 1: TO cable matches. Fiber config:`, fiberConfig);

        if (fiberConfig && !fiberConfig.is_tapped) {
          log(`[traceFromSource] ✅ Passthrough Case 1 (REVERSE): Signal on TO cable=${outgoingCableId}, fiber=${outgoingFiber} → goes back on FROM cable=${passThrough.from_cable_id}`);

          // This fiber is passed through from from_cable
          const fromCableId = passThrough.from_cable_id;
          const fromCable = cables.find(c => c.id === fromCableId);

          if (!fromCable) {
            return {
              found: false,
              segments: [],
              oltPower: 0,
              errorMessage: `Priechodný kábel #${fromCableId} sa nenašiel`,
            };
          }

          const outgoingCable = cables.find(c => c.id === outgoingCableId);
          const cableLoss = outgoingCable ? cableOpticalLength(outgoingCable) * (FIBER_LOSS_PER_KM / 1000) : 0;

          const cableSegment: OpticalPathSegment = {
            type: 'cable',
            cableId: outgoingCableId,
            cableName: `Cable #${outgoingCableId}`,
            fiber: outgoingFiber,
            loss: cableLoss,
            distance: outgoingCable?.length_meters,
            power: 0,
            description: `Cable #${outgoingCableId}, Fiber #${outgoingFiber} (${outgoingCable?.length_meters || 0}m)`,
          };

          const passThroughSegment: OpticalPathSegment = {
            type: 'passthrough',
            poleId: sourcePole.id,
            poleName: sourcePole.name,
            loss: 0.01, // Minimal loss for fiber bend
            power: 0,
            description: `Pass-through at ${sourcePole.name}`,
          };

          // Check if from_cable comes from OLT
          const oltPole = poles.find(p => p.id === fromCable.from_pole_id && p.pole_type === 'olt');
          if (oltPole) {
            log(`[traceFromSource] 🔍 FROM cable comes from OLT, tracing from OLT`);
            // This is a cable from OLT - trace from OLT directly
            const oltResult = traceFromOLT(oltPole, fromCableId, outgoingFiber, cables);
            if (!oltResult.found) {
              return oltResult;
            }
            return {
              found: true,
              segments: [...oltResult.segments, passThroughSegment, cableSegment],
              oltPower: oltResult.oltPower,
            };
          }

          log(`[traceFromSource] 🔍 Tracing upstream on FROM cable=${fromCableId}, fiber=${outgoingFiber}`);

          // Passthrough is bidirectional - trace on FROM cable in the direction where signal comes from
          // We need to find which pole the signal comes from on the FROM cable
          const fromCableDirection = getFiberDirection(fromCableId, outgoingFiber, cables, poles);

          if (fromCableDirection) {
            log(`[traceFromSource] 🔍 FROM cable direction: source=${fromCableDirection.sourcePoleId}, target=${fromCableDirection.targetPoleId}, reverse=${fromCableDirection.isReverse}`);

            // Use the SOURCE pole of the FROM fiber (where the fiber originates)
            const upstreamPole = poles.find(p => p.id === fromCableDirection.sourcePoleId);
            if (upstreamPole) {
              const upstreamResult = traceFromSource(
                upstreamPole,
                fromCableId,
                outgoingFiber,
                poles,
                cables,
                visited
              );

              if (!upstreamResult.found) {
                return upstreamResult;
              }

              return {
                found: true,
                segments: [...upstreamResult.segments, passThroughSegment, cableSegment],
                oltPower: upstreamResult.oltPower,
              };
            }
          }

          // Fallback: try both ends of FROM cable
          log(`[traceFromSource] 🔍 Trying both ends of FROM cable`);

          const fromPole = poles.find(p => p.id === fromCable.from_pole_id);
          if (fromPole) {
            const upstreamResult = traceFromSource(
              fromPole,
              fromCableId,
              outgoingFiber,
              poles,
              cables,
              visited
            );

            if (upstreamResult.found) {
              return {
                found: true,
                segments: [...upstreamResult.segments, passThroughSegment, cableSegment],
                oltPower: upstreamResult.oltPower,
              };
            }
          }

          const toPole = poles.find(p => p.id === fromCable.to_pole_id);
          if (toPole) {
            const upstreamResult = traceFromSource(
              toPole,
              fromCableId,
              outgoingFiber,
              poles,
              cables,
              visited
            );

            if (upstreamResult.found) {
              return {
                found: true,
                segments: [...upstreamResult.segments, passThroughSegment, cableSegment],
                oltPower: upstreamResult.oltPower,
              };
            }
          }
        }

        // Case 1b: Fiber is tapped in pass-through TO this cable
        if (fiberConfig && fiberConfig.is_tapped) {
          // Priechod má dva konce a pri odbočení sa vlákno prestrihne:
          // zvarený je len ten koniec, ktorý technik zvolil, druhý ostáva
          // voľný a signál na ňom nie je. Keď voľba chýba (staršie dáta),
          // platí pôvodné správanie.
          if (fiberConfig.tap_from_cable_id
              && fiberConfig.tap_from_cable_id !== outgoingCableId) {
            return {
              found: false,
              segments: [],
              oltPower: 0,
              errorMessage: `Vlákno #${outgoingFiber} je na tomto konci priechodu voľné — `
                + `odbočka ide z kábla #${fiberConfig.tap_from_cable_id}`,
            };
          }
          // This fiber is tapped to another cable
          const tapToCableId = fiberConfig.tap_to_cable_id;
          const tapToFiber = fiberConfig.tap_to_fiber;
          const tapLoss = fiberConfig.tap_loss_db || 0.1;

          if (!tapToCableId || !tapToFiber) {
            return {
              found: false,
              segments: [],
              oltPower: 0,
              errorMessage: `Neúplné nastavenie odpichu pre vlákno #${outgoingFiber}`,
            };
          }

          const tapCable = cables.find(c => c.id === tapToCableId);
          if (!tapCable) {
            return {
              found: false,
              segments: [],
              oltPower: 0,
              errorMessage: `Kábel odpichu #${tapToCableId} sa nenašiel`,
            };
          }

          const outgoingCable = cables.find(c => c.id === outgoingCableId);
          const cableLoss = outgoingCable ? cableOpticalLength(outgoingCable) * (FIBER_LOSS_PER_KM / 1000) : 0;

          const cableSegment: OpticalPathSegment = {
            type: 'cable',
            cableId: outgoingCableId,
            cableName: `Cable #${outgoingCableId}`,
            fiber: outgoingFiber,
            loss: cableLoss,
            distance: outgoingCable?.length_meters,
            power: 0,
            description: `Cable #${outgoingCableId}, Fiber #${outgoingFiber} (${outgoingCable?.length_meters || 0}m)`,
          };

          const tapSegment: OpticalPathSegment = {
            type: 'tap',
            poleId: sourcePole.id,
            poleName: sourcePole.name,
            loss: tapLoss,
            power: 0,
            description: `Tap at ${sourcePole.name} (${tapLoss} dB)`,
          };

          // Continue tracing on the tap cable
          const upstreamPole = poles.find(p => p.id === tapCable.from_pole_id);
          if (!upstreamPole) {
            return {
              found: false,
              segments: [],
              oltPower: 0,
              errorMessage: `Nadradený stĺpik #${tapCable.from_pole_id} sa nenašiel`,
            };
          }

          const upstreamResult = traceFromSource(
            upstreamPole,
            tapToCableId,
            tapToFiber,
            poles,
            cables,
            visited
          );

          if (!upstreamResult.found) {
            return upstreamResult;
          }

          return {
            found: true,
            segments: [...upstreamResult.segments, tapSegment, cableSegment],
            oltPower: upstreamResult.oltPower,
          };
        }
      }

      // Case 2: Signal comes on FROM cable, goes forward on TO cable (normal direction)
      if (passThrough.from_cable_id === outgoingCableId) {
        const fiberConfig = passThrough.fibers?.find(f => f.fiber_number === outgoingFiber);
        log(`[traceFromSource] 🔍 Case 2: FROM cable matches. Fiber config:`, fiberConfig);

        if (fiberConfig && !fiberConfig.is_tapped) {
          log(`[traceFromSource] ✅ Passthrough Case 2 (NORMAL): Signal on FROM cable=${outgoingCableId}, fiber=${outgoingFiber} → goes forward on TO cable=${passThrough.to_cable_id}`);

          // This fiber passes through to to_cable
          const toCableId = passThrough.to_cable_id;
          const toCable = cables.find(c => c.id === toCableId);

          if (!toCable) {
            return {
              found: false,
              segments: [],
              oltPower: 0,
              errorMessage: `Priechodný kábel #${toCableId} sa nenašiel`,
            };
          }

          const outgoingCable = cables.find(c => c.id === outgoingCableId);
          const cableLoss = outgoingCable ? cableOpticalLength(outgoingCable) * (FIBER_LOSS_PER_KM / 1000) : 0;

          const cableSegment: OpticalPathSegment = {
            type: 'cable',
            cableId: outgoingCableId,
            cableName: `Cable #${outgoingCableId}`,
            fiber: outgoingFiber,
            loss: cableLoss,
            distance: outgoingCable?.length_meters,
            power: 0,
            description: `Cable #${outgoingCableId}, Fiber #${outgoingFiber} (${outgoingCable?.length_meters || 0}m)`,
          };

          const passThroughSegment: OpticalPathSegment = {
            type: 'passthrough',
            poleId: sourcePole.id,
            poleName: sourcePole.name,
            loss: 0.01, // Minimal loss for fiber bend
            power: 0,
            description: `Pass-through at ${sourcePole.name}`,
          };

          log(`[traceFromSource] 🔍 Tracing upstream on TO cable=${toCableId}, fiber=${outgoingFiber}`);

          // Passthrough is bidirectional - trace on TO cable in the direction where signal comes from
          const toCableDirection = getFiberDirection(toCableId, outgoingFiber, cables, poles);

          if (toCableDirection) {
            log(`[traceFromSource] 🔍 TO cable direction: source=${toCableDirection.sourcePoleId}, target=${toCableDirection.targetPoleId}, reverse=${toCableDirection.isReverse}`);

            // Use the SOURCE pole of the TO fiber (where the fiber originates)
            const upstreamPole = poles.find(p => p.id === toCableDirection.sourcePoleId);
            if (upstreamPole) {
              const upstreamResult = traceFromSource(
                upstreamPole,
                toCableId,
                outgoingFiber,
                poles,
                cables,
                visited
              );

              if (upstreamResult.found) {
                return {
                  found: true,
                  segments: [...upstreamResult.segments, passThroughSegment, cableSegment],
                  oltPower: upstreamResult.oltPower,
                };
              }
            }
          }

          // Fallback: try both ends of TO cable
          log(`[traceFromSource] 🔍 Trying both ends of TO cable`);

          const fromPole = poles.find(p => p.id === toCable.from_pole_id);
          if (fromPole) {
            const upstreamResult = traceFromSource(
              fromPole,
              toCableId,
              outgoingFiber,
              poles,
              cables,
              visited
            );

            if (upstreamResult.found) {
              return {
                found: true,
                segments: [...upstreamResult.segments, passThroughSegment, cableSegment],
                oltPower: upstreamResult.oltPower,
              };
            }
          }

          const toPole = poles.find(p => p.id === toCable.to_pole_id);
          if (toPole) {
            const upstreamResult = traceFromSource(
              toPole,
              toCableId,
              outgoingFiber,
              poles,
              cables,
              visited
            );

            if (upstreamResult.found) {
              return {
                found: true,
                segments: [...upstreamResult.segments, passThroughSegment, cableSegment],
                oltPower: upstreamResult.oltPower,
              };
            }
          }
        }

        // Case 2b: Fiber is tapped in pass-through FROM this cable
        if (fiberConfig && fiberConfig.is_tapped) {
          // To isté z druhej strany — viď Case 1b.
          if (fiberConfig.tap_from_cable_id
              && fiberConfig.tap_from_cable_id !== outgoingCableId) {
            return {
              found: false,
              segments: [],
              oltPower: 0,
              errorMessage: `Vlákno #${outgoingFiber} je na tomto konci priechodu voľné — `
                + `odbočka ide z kábla #${fiberConfig.tap_from_cable_id}`,
            };
          }
          // This fiber is tapped to another cable
          const tapToCableId = fiberConfig.tap_to_cable_id;
          const tapToFiber = fiberConfig.tap_to_fiber;
          const tapLoss = fiberConfig.tap_loss_db || 0.1;

          if (!tapToCableId || !tapToFiber) {
            return {
              found: false,
              segments: [],
              oltPower: 0,
              errorMessage: `Neúplné nastavenie odpichu pre vlákno #${outgoingFiber}`,
            };
          }

          const tapCable = cables.find(c => c.id === tapToCableId);
          if (!tapCable) {
            return {
              found: false,
              segments: [],
              oltPower: 0,
              errorMessage: `Kábel odpichu #${tapToCableId} sa nenašiel`,
            };
          }

          const outgoingCable = cables.find(c => c.id === outgoingCableId);
          const cableLoss = outgoingCable ? cableOpticalLength(outgoingCable) * (FIBER_LOSS_PER_KM / 1000) : 0;

          const cableSegment: OpticalPathSegment = {
            type: 'cable',
            cableId: outgoingCableId,
            cableName: `Cable #${outgoingCableId}`,
            fiber: outgoingFiber,
            loss: cableLoss,
            distance: outgoingCable?.length_meters,
            power: 0,
            description: `Cable #${outgoingCableId}, Fiber #${outgoingFiber} (${outgoingCable?.length_meters || 0}m)`,
          };

          const tapSegment: OpticalPathSegment = {
            type: 'tap',
            poleId: sourcePole.id,
            poleName: sourcePole.name,
            loss: tapLoss,
            power: 0,
            description: `Tap at ${sourcePole.name} (${tapLoss} dB)`,
          };

          // Continue tracing on the tap cable
          const upstreamPole = poles.find(p => p.id === tapCable.from_pole_id);
          if (!upstreamPole) {
            return {
              found: false,
              segments: [],
              oltPower: 0,
              errorMessage: `Nadradený stĺpik #${tapCable.from_pole_id} sa nenašiel`,
            };
          }

          const upstreamResult = traceFromSource(
            upstreamPole,
            tapToCableId,
            tapToFiber,
            poles,
            cables,
            visited
          );

          if (!upstreamResult.found) {
            return upstreamResult;
          }

          return {
            found: true,
            segments: [...upstreamResult.segments, tapSegment, cableSegment],
            oltPower: upstreamResult.oltPower,
          };
        }
      }
    }
  }

  // No connection found
  return {
    found: false,
    segments: [],
    oltPower: 0,
    errorMessage: `Na ${sourcePole.name} nie je výstupné prepojenie pre kábel #${outgoingCableId}, vlákno #${outgoingFiber}`,
  };
}

function traceBackwards(
  currentPole: Pole,
  incomingCableId: number,
  incomingFiber: number,
  poles: Pole[],
  cables: Cable[],
  visited: Set<string>
): TraceResult {
  const visitKey = `${currentPole.id}-${incomingCableId}-${incomingFiber}`;
  if (visited.has(visitKey)) {
    return {
      found: false,
      segments: [],
      oltPower: 0,
      errorMessage: 'Zistený kruhový odkaz',
    };
  }
  visited.add(visitKey);

  // Check if this is an OLT
  if (currentPole.pole_type === 'olt') {
    return traceFromOLT(currentPole, incomingCableId, incomingFiber, cables);
  }

  // Check cassette for connections
  const cassette = currentPole.cassette;
  if (!cassette) {
    return {
      found: false,
      segments: [],
      oltPower: 0,
      errorMessage: `Stĺpik ${currentPole.name} nemá kazetu`,
    };
  }

  // Check splitters
  if (cassette.splitters) {
    for (const splitter of cassette.splitters) {
      // Check if this fiber is an output of this splitter
      const outputIndex = splitter.outputs?.findIndex(
        out => out.cable_id === incomingCableId && out.fiber === incomingFiber
      );

      if (outputIndex !== undefined && outputIndex >= 0) {
        // Found! This fiber comes from this splitter
        const splitterLoss = getSplitterOutputLoss(splitter.type, outputIndex);
        const segment: OpticalPathSegment = {
          type: 'splitter',
          poleId: currentPole.id,
          poleName: currentPole.name,
          loss: splitterLoss,
          power: 0, // Will be calculated later
          description: `Splitter ${splitter.type} at ${currentPole.name}`,
        };

        // Now trace the input of this splitter
        if (splitter.input_source_type === 'olt_port') {
          // This shouldn't happen for non-OLT poles
          return {
            found: false,
            segments: [],
            oltPower: 0,
            errorMessage: 'Neplatný vstup splittera',
          };
        } else if (splitter.input_source_type === 'cable') {
          const inputCableId = splitter.input_cable_id!;
          const inputFiber = splitter.input_fiber!;
          const inputCable = cables.find(c => c.id === inputCableId);

          if (!inputCable) {
            return {
              found: false,
              segments: [],
              oltPower: 0,
              errorMessage: `Vstupný kábel #${inputCableId} sa nenašiel`,
            };
          }

          // Add cable segment
          const cableLoss = (inputCable.length_meters || 0) * (FIBER_LOSS_PER_KM / 1000);
          const cableSegment: OpticalPathSegment = {
            type: 'cable',
            cableId: inputCable.id,
            cableName: `Cable #${inputCable.id}`,
            fiber: inputFiber,
            loss: cableLoss,
            distance: inputCable.length_meters,
            power: 0,
            description: `Cable #${inputCable.id}, Fiber #${inputFiber} (${inputCable.length_meters}m)`,
          };

          // Find source pole of this cable
          const sourcePole = poles.find(p => p.id === inputCable.from_pole_id);
          if (!sourcePole) {
            return {
              found: false,
              segments: [],
              oltPower: 0,
              errorMessage: `Zdrojový stĺpik #${inputCable.from_pole_id} sa nenašiel`,
            };
          }

          // Recursively trace from source pole
          const upstreamResult = traceBackwards(
            sourcePole,
            inputCableId,
            inputFiber,
            poles,
            cables,
            visited
          );

          if (!upstreamResult.found) {
            return upstreamResult;
          }

          return {
            found: true,
            segments: [segment, cableSegment, ...upstreamResult.segments],
            oltPower: upstreamResult.oltPower,
          };
        }
      }
    }
  }

  // Check pass-through cables
  if (cassette.pass_through_cables) {
    for (const passThrough of cassette.pass_through_cables) {
      if (passThrough.to_cable_id === incomingCableId) {
        // Check if this fiber is passed through
        const fiberConfig = passThrough.fibers?.find((f: any) => f.fiber_number === incomingFiber);
        if (fiberConfig && !fiberConfig.is_tapped) {
          // This fiber is passed through from from_cable
          const fromCableId = passThrough.from_cable_id;
          const fromCable = cables.find(c => c.id === fromCableId);

          if (!fromCable) {
            return {
              found: false,
              segments: [],
              oltPower: 0,
              errorMessage: `Priechodný kábel #${fromCableId} sa nenašiel`,
            };
          }

          const passThroughSegment: OpticalPathSegment = {
            type: 'passthrough',
            poleId: currentPole.id,
            poleName: currentPole.name,
            loss: 0.01, // Minimal loss for fiber bend
            power: 0,
            description: `Pass-through at ${currentPole.name}`,
          };

          const cableLoss = (fromCable.length_meters || 0) * (FIBER_LOSS_PER_KM / 1000);
          const cableSegment: OpticalPathSegment = {
            type: 'cable',
            cableId: fromCable.id,
            cableName: `Cable #${fromCable.id}`,
            fiber: incomingFiber,
            loss: cableLoss,
            distance: fromCable.length_meters,
            power: 0,
            description: `Cable #${fromCable.id}, Fiber #${incomingFiber} (${fromCable.length_meters}m)`,
          };

          const sourcePole = poles.find(p => p.id === fromCable.from_pole_id);
          if (!sourcePole) {
            return {
              found: false,
              segments: [],
              oltPower: 0,
              errorMessage: `Zdrojový stĺpik #${fromCable.from_pole_id} sa nenašiel`,
            };
          }

          const upstreamResult = traceBackwards(
            sourcePole,
            fromCableId,
            incomingFiber,
            poles,
            cables,
            visited
          );

          if (!upstreamResult.found) {
            return upstreamResult;
          }

          return {
            found: true,
            segments: [passThroughSegment, cableSegment, ...upstreamResult.segments],
            oltPower: upstreamResult.oltPower,
          };
        }
      }
    }
  }

  // No connection found
  return {
    found: false,
    segments: [],
    oltPower: 0,
    errorMessage: `Na ${currentPole.name} nie je prepojenie pre kábel #${incomingCableId}, vlákno #${incomingFiber}`,
  };
}

function traceFromOLT(
  olt: Pole,
  outgoingCableId: number,
  outgoingFiber: number,
  cables: Cable[]
): TraceResult {
  const cassette = olt.cassette;
  if (!cassette) {
    return {
      found: false,
      segments: [],
      oltPower: 0,
      errorMessage: 'OLT nemá nastavenú kazetu',
    };
  }

  // Check OLT splitters
  if (cassette.splitters) {
    for (let splitterIndex = 0; splitterIndex < cassette.splitters.length; splitterIndex++) {
      const splitter = cassette.splitters[splitterIndex];
      const outputIndex = splitter.outputs?.findIndex(
        (out: any) => out.cable_id === outgoingCableId && out.fiber === outgoingFiber
      );

      if (outputIndex !== undefined && outputIndex >= 0) {
        // Found! This fiber comes from this OLT splitter
        const splitterLoss = getSplitterOutputLoss(splitter.type, outputIndex);

        // Add cable segment
        const outgoingCable = cables.find(c => c.id === outgoingCableId);
        const cableLoss = outgoingCable ? cableOpticalLength(outgoingCable) * (FIBER_LOSS_PER_KM / 1000) : 0;

        const cableSegment: OpticalPathSegment = {
          type: 'cable',
          cableId: outgoingCableId,
          cableName: `Cable #${outgoingCableId}`,
          fiber: outgoingFiber,
          loss: cableLoss,
          distance: outgoingCable?.length_meters,
          power: 0,
          description: `Cable #${outgoingCableId}, Fiber #${outgoingFiber} (${outgoingCable?.length_meters || 0}m)`,
        };

        const splitterSegment: OpticalPathSegment = {
          type: 'olt_splitter',
          poleId: olt.id,
          poleName: olt.name,
          loss: splitterLoss,
          power: 0,
          description: `OLT Splitter ${splitter.type}`,
        };

        // Check if this splitter is fed from another splitter or from OLT port
        if (splitter.input_source_type === 'splitter_output') {
          // This splitter is fed from another splitter in the same OLT
          const sourceSplitterIndex = splitter.input_splitter_index;
          const sourceOutputIndex = splitter.input_splitter_output;

          if (sourceSplitterIndex === undefined || sourceOutputIndex === undefined) {
            return {
              found: false,
              segments: [],
              oltPower: 0,
              errorMessage: 'Neplatné nastavenie výstupu splittera v OLT',
            };
          }

          const sourceSplitter = cassette.splitters?.[sourceSplitterIndex];
          if (!sourceSplitter) {
            return {
              found: false,
              segments: [],
              oltPower: 0,
              errorMessage: `Zdrojový splitter #${sourceSplitterIndex} sa v OLT nenašiel`,
            };
          }

          // Source splitter must be fed from OLT port
          if (sourceSplitter.input_source_type !== 'olt_port') {
            return {
              found: false,
              segments: [],
              oltPower: 0,
              errorMessage: 'Kaskáda splitrov hlbšia než 2 úrovne nie je podporovaná',
            };
          }

          const portNumber = sourceSplitter.input_olt_port || 1;
          const portConfig = cassette.olt_port_configs?.find((pc: any) => pc.port_number === portNumber);
          const oltPower = portConfig?.power_dbm || 3.0;

          const sourceSplitterLoss = getSplitterLoss(sourceSplitter.type);

          const segments: OpticalPathSegment[] = [
            {
              type: 'olt_port',
              poleId: olt.id,
              poleName: olt.name,
              loss: 0,
              power: oltPower,
              description: `OLT ${olt.name} Port ${portNumber} (${oltPower} dBm)`,
            },
            {
              type: 'olt_splitter',
              poleId: olt.id,
              poleName: olt.name,
              loss: sourceSplitterLoss,
              power: 0,
              description: `OLT Splitter ${sourceSplitter.type} (Port ${portNumber})`,
            },
            splitterSegment,
            cableSegment,
          ];

          return {
            found: true,
            segments,
            oltPower,
          };
        } else {
          // Direct from OLT port
          const portNumber = splitter.input_olt_port || 1;
          const portConfig = cassette.olt_port_configs?.find((pc: any) => pc.port_number === portNumber);
          const oltPower = portConfig?.power_dbm || 3.0;

          const segments: OpticalPathSegment[] = [
            {
              type: 'olt_port',
              poleId: olt.id,
              poleName: olt.name,
              loss: 0,
              power: oltPower,
              description: `OLT ${olt.name} Port ${portNumber} (${oltPower} dBm)`,
            },
            splitterSegment,
            cableSegment,
          ];

          return {
            found: true,
            segments,
            oltPower,
          };
        }
      }
    }
  }

  // Check OLT splices
  if (cassette.splices) {
    for (const splice of cassette.splices) {
      if (splice.to_cable_id === outgoingCableId && splice.to_fiber === outgoingFiber) {
        // Found! This fiber comes from this OLT splice
        if (splice.from_source_type === 'olt_port') {
          const portNumber = splice.from_olt_port || 1;
          const portConfig = cassette.olt_port_configs?.find((pc: any) => pc.port_number === portNumber);
          const oltPower = portConfig?.power_dbm || 3.0;
          const spliceLoss = splice.loss_db || 0.1;

          const outgoingCable = cables.find(c => c.id === outgoingCableId);
          const cableLoss = outgoingCable ? cableOpticalLength(outgoingCable) * (FIBER_LOSS_PER_KM / 1000) : 0;

          const segments: OpticalPathSegment[] = [
            {
              type: 'olt_port',
              poleId: olt.id,
              poleName: olt.name,
              loss: 0,
              power: oltPower,
              description: `OLT ${olt.name} Port ${portNumber} (${oltPower} dBm)`,
            },
            {
              type: 'splice',
              poleId: olt.id,
              poleName: olt.name,
              loss: spliceLoss,
              power: 0,
              description: `Splice at ${olt.name}`,
            },
            {
              type: 'cable',
              cableId: outgoingCableId,
              cableName: `Cable #${outgoingCableId}`,
              fiber: outgoingFiber,
              loss: cableLoss,
              distance: outgoingCable?.length_meters,
              power: 0,
              description: `Cable #${outgoingCableId}, Fiber #${outgoingFiber} (${outgoingCable?.length_meters || 0}m)`,
            },
          ];

          return {
            found: true,
            segments,
            oltPower,
          };
        }
      }
    }
  }

  // Check direct port connections (no splitter/splice)
  // TODO: Implement if needed

  return {
    found: false,
    segments: [],
    oltPower: 0,
    errorMessage: `Nenašlo sa prepojenie k OLT pre kábel #${outgoingCableId}, vlákno #${outgoingFiber}`,
  };
}

/**
 * Format optical path result for display
 */
export function formatOpticalPath(result: OpticalPathResult): string {
  if (!result.found || !result.isValid) {
    return result.errorMessage || 'Trasa sa nenašla';
  }

  const lines: string[] = [];
  lines.push(`Signal: ${result.oltPower.toFixed(1)} dBm → ${result.finalPower.toFixed(1)} dBm`);
  lines.push(`Total Loss: ${result.totalLoss.toFixed(2)} dB`);
  lines.push('');
  lines.push('Path:');

  result.segments.forEach((segment, index) => {
    const arrow = index < result.segments.length - 1 ? ' →' : '';
    lines.push(`  ${segment.description} (-${segment.loss.toFixed(2)} dB)${arrow}`);
  });

  return lines.join('\n');
}

